// @workflow assistant.attachments, assistant.feedback, assistant.history, assistant.send-message
/**
 * The AVA Assistant transport to the aiva.api agent loop: fetch + a streamed body read as
 * Server-Sent Events. It emits exactly the events of the contract
 * (.claude/features/AVA_ASSISTANT_EVENT_CONTRACT.md) and returns a run handle:
 * start() → { stop(), resolve(requestId, response) }.
 *
 * - Stop aborts the request and emits message.end { stopped } itself (the server's may never come).
 * - A stream that drops without message.end / message.pause / error reconnects from the last seq
 *   (GET api/assistant/runs/{runId}/events?after={seq}), up to three times with backoff.
 * - A 401 asks refreshAuth() once and resends; other failures become a contract `error` event.
 *
 * fetch, headers, token refresh and sleep are injected, so node --test drives it with fakes.
 * No Vue or path-alias imports.
 */
import { createSseParser } from '../domain/sseParser.js';
import { EVENT } from '../domain/events.js';

/** What the user reads when the transport fails (the server's own errors arrive as events). */
export const SSE_MESSAGES = Object.freeze({
  BUSY: 'AVA is still answering in this chat. Try again in a moment.',
  LOST: 'The connection to AVA was lost. Try again.',
  FAILED: "AVA couldn't answer just now. Try again.",
  REJECTED: "AVA couldn't take that request.",
  SIGNED_OUT: 'Your session ended. Sign in again to keep chatting.',
});

const RECONNECT_DELAYS_MS = Object.freeze([500, 1500, 3000]);
const ENDING = Object.freeze([EVENT.MESSAGE_END, EVENT.ERROR]);
const HTTP = Object.freeze({ UNAUTHORIZED: 401, CONFLICT: 409, SERVER_ERROR: 500 });
const SSE_ACCEPT = 'text/event-stream';
const JSON_ACCEPT = 'application/json';

const defaultSleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const isAbort = (err, signal) => signal.aborted || (err && err.name === 'AbortError');

/** The user-facing error for a refused request: [message, retryable]. */
function failureFor(status) {
  if (status === HTTP.CONFLICT) return [SSE_MESSAGES.BUSY, true];
  if (status === HTTP.UNAUTHORIZED) return [SSE_MESSAGES.SIGNED_OUT, false];
  if (status >= HTTP.SERVER_ERROR) return [SSE_MESSAGES.FAILED, true];
  return [SSE_MESSAGES.REJECTED, false];
}

/** The `code` of a refused request's JSON body, or null (a web server's own page has none). */
async function refusalCode(response) {
  try {
    const body = await response.json();
    return body && typeof body.code === 'string' ? body.code : null;
  } catch (err) {
    return null;
  }
}

/**
 * @param {{
 *   baseUrl: string,
 *   fetchImpl?: typeof fetch,
 *   getHeaders?: () => Record<string, string>,
 *   refreshAuth?: () => Promise<boolean>,
 *   sleep?: (ms: number) => Promise<void>,
 *   reconnectDelaysMs?: number[],
 * }} deps
 */
export function createSseAgentClient({
  baseUrl,
  fetchImpl = (...args) => fetch(...args),
  getHeaders = () => ({}),
  refreshAuth = async () => false,
  sleep = defaultSleep,
  reconnectDelaysMs = RECONNECT_DELAYS_MS,
}) {
  const api = `${String(baseUrl || '').replace(/\/+$/, '')}/api/assistant`;
  const conversationUrl = (id) => `${api}/conversations/${encodeURIComponent(id)}`;

  /** One request, resent once after a successful token refresh when the server says 401. */
  async function request(url, { method = 'POST', body, signal, accept = SSE_ACCEPT, keepalive = false } = {}) {
    const init = () => ({
      method,
      signal,
      keepalive,
      headers: { ...(body === undefined ? {} : { 'Content-Type': JSON_ACCEPT }), Accept: accept, ...getHeaders() },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const first = await fetchImpl(url, init());
    if (first.status !== HTTP.UNAUTHORIZED || !(await refreshAuth())) return first;
    return fetchImpl(url, init());
  }

  /** One reply: its streams (first, resumed after an answer, reconnected) share this state. */
  function run(firstUrl, firstBody, conversationId, onEvent) {
    const controller = new AbortController();
    const { signal } = controller;
    const answered = new Set();
    let messageId = null;
    let runId = null;
    let lastSeq = 0;
    let ended = false;
    let paused = false;
    let current = Promise.resolve();

    function deliver(event) {
      if (ended || !event || typeof event.type !== 'string') return;
      if (Number.isInteger(event.seq)) {
        if (event.seq <= lastSeq) return;
        lastSeq = event.seq;
      }
      if (event.type === EVENT.MESSAGE_START) messageId = event.messageId;
      if (typeof event.runId === 'string') runId = event.runId;
      if (ENDING.includes(event.type)) ended = true;
      if (event.type === EVENT.MESSAGE_PAUSE) paused = true;
      onEvent(event);
    }

    /** `code`: the server's refusal code, when it sent one (e.g. attachment.not_found), for the store. */
    function fail(message, retryable, code = null) {
      deliver({ type: EVENT.ERROR, ...(messageId ? { messageId } : {}), message, retryable, ...(code ? { code } : {}) });
    }

    /** Reads one streamed body to its end (or until the reply ended). */
    async function read(response) {
      const parser = createSseParser(({ data }) => {
        let event = null;
        try {
          event = JSON.parse(data);
        } catch (err) {
          return; // a malformed frame is skipped; the next one can still be read
        }
        deliver(event);
      });
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.push(decoder.decode(value, { stream: true }));
        if (ended) {
          reader.cancel().catch(() => {});
          break;
        }
      }
      parser.end();
    }

    async function reconnect() {
      if (!runId) {
        fail(SSE_MESSAGES.LOST, true);
        return;
      }
      for (const delay of reconnectDelaysMs) {
        await sleep(delay);
        if (ended || signal.aborted) return;
        try {
          const response = await request(`${api}/runs/${encodeURIComponent(runId)}/events?after=${lastSeq}`, { method: 'GET', signal });
          if (response.ok) {
            await read(response);
            if (ended || paused) return;
          }
        } catch (err) {
          if (isAbort(err, signal)) return;
        }
      }
      fail(SSE_MESSAGES.LOST, true);
    }

    async function stream(url, body) {
      paused = false;
      try {
        const response = await request(url, { body, signal });
        if (!response.ok) {
          const [message, retryable] = failureFor(response.status);
          fail(message, retryable, await refusalCode(response));
          return;
        }
        await read(response);
      } catch (err) {
        if (isAbort(err, signal)) return;
        // A network drop mid-stream: fall through to reconnect.
      }
      if (ended || paused || signal.aborted) return;
      await reconnect();
    }

    current = stream(firstUrl, firstBody);

    return {
      stop() {
        if (ended) return;
        controller.abort();
        if (messageId) deliver({ type: EVENT.MESSAGE_END, messageId, stopped: true });
        ended = true;
      },
      /** Answers a client.request; the reply resumes on a new stream. Idempotent per request. */
      resolve(requestId, response) {
        if (ended || answered.has(requestId)) return;
        answered.add(requestId);
        current = stream(`${conversationUrl(conversationId)}/client-requests/${encodeURIComponent(requestId)}`, response);
      },
      /** Settles when the current stream (and any reconnect) is over. For tests and callers that wait. */
      settled() {
        return current;
      },
    };
  }

  return {
    /**
     * @param {string} prompt
     * @param {(event: object) => void} onEvent
     * @param {{ conversationId: string, context?: object, segments?: Array, client?: { uiTools: string[] }, attachments?: string[], retry?: { versionOf: string, reason?: string, comment?: string } }} options
     *   client = what this UI build can run (contract: "UI tools: who decides what"), sent on every message
     *   attachments = the ids of the ready files (contract "Attachments"); no key when there are none
     */
    start(prompt, onEvent, { conversationId, context = null, segments, client, attachments = [], retry = null }) {
      const base = conversationUrl(conversationId);
      if (retry) {
        const body = {};
        if (retry.reason) body.reason = retry.reason;
        if (retry.comment) body.comment = retry.comment;
        if (client) body.client = client;
        return run(`${base}/messages/${encodeURIComponent(retry.versionOf)}/regenerate`, body, conversationId, onEvent);
      }
      const files = attachments && attachments.length ? { attachments } : {};
      return run(`${base}/messages`, { text: prompt, ...(segments ? { segments } : {}), context, ...(client ? { client } : {}), ...files }, conversationId, onEvent);
    },

    /**
     * The caller's own conversations, newest first (aiva.api B1).
     * @returns {Promise<{ items: Array<{ id: string, title: string, createdAt: string, updatedAt: string }>, total: number }>}
     */
    async listConversations({ page = 1, itemsPerPage = 30 } = {}) {
      const response = await request(`${api}/conversations?page=${encodeURIComponent(page)}&itemsPerPage=${encodeURIComponent(itemsPerPage)}`, { method: 'GET', accept: JSON_ACCEPT });
      if (!response.ok) throw new Error(`Conversations could not be loaded (${response.status})`);
      return response.json();
    },

    /** A conversation's stored events in Seq order, for replay. */
    async conversationEvents(conversationId) {
      const response = await request(`${conversationUrl(conversationId)}/events`, { method: 'GET', accept: JSON_ACCEPT });
      if (!response.ok) throw new Error(`The conversation could not be loaded (${response.status})`);
      const body = await response.json();
      return Array.isArray(body && body.events) ? body.events : [];
    },

    /**
     * Deletes a conversation for the user: the server archives it (a soft delete; it is purged with
     * the history retention). keepalive lets it finish while the page is closing.
     */
    async archiveConversation(conversationId) {
      const response = await request(`${conversationUrl(conversationId)}/archive`, { accept: JSON_ACCEPT, keepalive: true });
      if (!response.ok) throw new Error(`The conversation could not be deleted (${response.status})`);
    },

    /** Saves a rating. Rejects when the server refuses it, so the store can roll back. */
    async sendFeedback({ conversationId, messageId, feedback }) {
      const url = `${conversationUrl(conversationId)}/messages/${encodeURIComponent(messageId)}/feedback`;
      const response = await request(url, { body: feedback, accept: JSON_ACCEPT });
      if (!response.ok) throw new Error(`Feedback was not saved (${response.status})`);
    },
  };
}
